import { css, cx } from '@emotion/css';
import { useMemo } from 'react';
import { IoCalendarOutline, IoLocationOutline } from 'react-icons/io5';
import type { HTMLAttributes } from 'react';
import { useGetUserTicketEvents } from '../../hooks';
import { eventsService } from '../../../../api/services/events';
import { useQuery } from '@tanstack/react-query';
import { formatTrustedDate } from '../../../../shared/utils/datetime';

// Shared section styles similar to profile-info and profile-orders-list
const container = css({
  backgroundColor: 'var(--primary-color)',
  borderRadius: 'var(--spacing-sm)',
  padding: 'var(--spacing-lg)',
  boxShadow: 'var(--shadow-200)',
  '@media (max-width: 480px)': { padding: 'var(--spacing-md)' },
  '@media (max-width: 360px)': { padding: 'var(--spacing-sm)' },
});

const header = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 'var(--spacing-lg)',
  '@media (max-width: 480px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 'var(--spacing-sm)',
  },
});

const title = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
  fontSize: 'var(--fs-450)',
  fontWeight: 600,
  color: 'var(--g-800)',
  marginBottom: 0,
  '@media (max-width: 480px)': { fontSize: 'var(--fs-400)' },
});

const schedulesGrid = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: 'var(--spacing-md)',
  '@media (max-width: 96.8rem)': {
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
  },
  '@media (max-width: 480px)': {
    gridTemplateColumns: '1fr',
  },
});

const scheduleCard = css({
  backgroundColor: 'var(--primary-color)',
  borderRadius: 'var(--spacing-xs)',
  border: '1px solid var(--g-200)',
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  overflow: 'hidden',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: 'var(--shadow-300)',
    borderColor: 'var(--g-300)',
  },
});

const imageWrapper = css({
  width: '100%',
  height: '180px',
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: 'var(--pc-200)',
});

const image = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
});

const imagePlaceholder = css({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 'var(--fs-700)',
  fontWeight: 700,
  color: 'var(--pc-600)',
  backgroundColor: 'var(--pc-200)',
});

const scheduleContent = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-sm)',
  padding: 'var(--spacing-md)',
});

const scheduleTitle = css({
  fontSize: 'var(--fs-450)',
  fontWeight: 600,
  color: 'var(--g-800)',
});

const scheduleMeta = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
  color: 'var(--g-600)',
  fontSize: 'var(--fs-350)',
});

const countsRow = css({
  display: 'flex',
  gap: 'var(--spacing-sm)',
  marginTop: 'var(--spacing-sm)',
});

const badge = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--spacing-2xs)',
  padding: '2px 8px',
  borderRadius: '999px',
  fontSize: 'var(--fs-300)',
  fontWeight: 600,
});
const badgeIssued = css({ backgroundColor: 'rgba(16,185,129,0.12)', color: '#059669' });
const badgeUsed = css({ backgroundColor: 'rgba(59,130,246,0.12)', color: '#1D4ED8' });
const badgeRevoked = css({ backgroundColor: 'rgba(239,68,68,0.12)', color: '#B91C1C' });

function useSchedulesDetails(scheduleIds: string[]) {
  return useQuery({
    queryKey: ['schedules', { ids: scheduleIds }],
    queryFn: async () => {
      const results = await Promise.all(
        scheduleIds.map(async (id) => {
          try {
            const { schedule } = await eventsService.getScheduleById(id);
            return schedule;
          } catch {
            return null;
          }
        })
      );
      return results.filter(Boolean);
    },
    enabled: scheduleIds.length > 0,
  });
}

export interface ProfileTicketsListProps extends HTMLAttributes<HTMLDivElement> {
  includeRevoked?: boolean;
  onScheduleClick?: (scheduleId: string) => void;
}

export function ProfileTicketsList({ includeRevoked = false, onScheduleClick, className, style, ...props }: ProfileTicketsListProps) {
  const { data: eventsAgg, isLoading } = useGetUserTicketEvents(includeRevoked);

  const allScheduleIds = useMemo(() => {
    return (eventsAgg?.events || []).flatMap(e => e.scheduleIds);
  }, [eventsAgg]);

  const { data: schedules = [] } = useSchedulesDetails(allScheduleIds);

  const getPosterUrl = (schedule: any): string | undefined => {
    const poster = schedule?.event?.posterImage as { name?: string; extension?: string; url?: string } | undefined;
    if (!poster) return undefined;
    if (poster.url) return poster.url;
    if (poster.name && poster.extension) {
      const ext = poster.extension.startsWith('.') ? poster.extension : `.${poster.extension}`;
      return `/assets/${poster.name}${ext}`;
    }
    return undefined;
  };

  const formatScheduleDate = (iso: string): string => {
    return formatTrustedDate(iso);
  };

  const handleClick = (scheduleId: string) => {
    if (onScheduleClick) onScheduleClick(scheduleId);
  };

  return (
    <section className={cx(container, className)} style={style} {...props}>
      <div className={header}>
        <h2 className={title}>Meus ingressos</h2>
      </div>

      {isLoading && <p>Carregando ingressos...</p>}

      {!isLoading && schedules.length === 0 && (
        <p>Nenhum ingresso encontrado.</p>
      )}

      {!isLoading && schedules.length > 0 && (
        <div className={schedulesGrid}>
          {schedules.map((schedule) => {
            const eventTitle = schedule!.event?.title || 'Evento';
            const locationName = schedule!.location?.name || 'Local';
            const counts = (eventsAgg?.events || []).reduce(
              (acc, evt) => {
                const scheduleIds = evt?.scheduleIds || [];
                if (scheduleIds.includes(schedule!.id)) {
                    const by = evt?.byStatus || {} as Record<string, number>;
                    acc.issued += by['issued'] ?? 0;
                    acc.used += by['used'] ?? 0;
                    acc.revoked += by['revoked'] ?? 0;
                }
                return acc;
              },
              { issued: 0, used: 0, revoked: 0 }
            );

            const posterUrl = getPosterUrl(schedule);
            return (
              <div key={schedule!.id} className={scheduleCard} onClick={() => handleClick(schedule!.id)}>
                <div className={imageWrapper}>
                  {posterUrl ? (
                    <img src={posterUrl} alt={eventTitle} className={image} loading="lazy" />
                  ) : (
                    <div className={imagePlaceholder}>{eventTitle?.charAt(0).toUpperCase() || 'E'}</div>
                  )}
                </div>
                <div className={scheduleContent}>
                  <h3 className={scheduleTitle}>{eventTitle}</h3>
                  <div className={scheduleMeta}>
                    <IoCalendarOutline />
                    <span>{formatScheduleDate(schedule!.date)}</span>
                  </div>
                  <div className={scheduleMeta}>
                    <IoLocationOutline />
                    <span>{locationName}</span>
                  </div>
                  <div className={countsRow}>
                    <span className={cx(badge, badgeIssued)}>Emitidos: {counts.issued}</span>
                    <span className={cx(badge, badgeUsed)}>Usados: {counts.used}</span>
                    <span className={cx(badge, badgeRevoked)}>Revogados: {counts.revoked}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default ProfileTicketsList;
